import React, { MouseEventHandler, useState } from 'react';
import styled from 'styled-components/macro';
import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useJoinUrl } from 'src/client/components/buttons/JoinButton';
import { MetartNetworkModal } from 'src/client/components/modals/MetartNetworkModal/MetartNetworkModal';
import { useUpgradeStrip } from './helpers/useUpgradeStrip';
import { useShowSfwSite } from 'src/client/helpers/useFeatureFlags';
import { NonNudeInfoStrip } from './NonNudeInfoStrip';
import { UPGRADE_STRIP } from 'src/client/constants/joinCampaigns';
import { useSelector } from 'src/client/redux';
import { DESKTOP, TABLET_PORTRAIT } from 'src/client/constants/mediaQueries';
import { MetArtNetwork } from 'src/client/constants/sites';

export const UpgradeStrip = () => {
  const user = useSelector((state) => state.auth.user);
  const [upgradeStripOpen, closeUpgradeStrip] = useUpgradeStrip();
  const showSfwSite = useShowSfwSite();
  const [showModal, setShowModal] = useState(false);
  const [joinUrl] = useJoinUrl({
    campaign: UPGRADE_STRIP,
    extraParams: !user?.hasNetworkSubscription
      ? {
          site: MetArtNetwork.abbreviation,
        }
      : undefined,
  });

  // This _might_ cause the non-nude strip to show up at an unexpected time in
  // the future if we offer the ability to dismiss the age verify modal and a
  // member is browsing the blurred site.
  if (showSfwSite) {
    return <NonNudeInfoStrip />;
  }

  if (!upgradeStripOpen) return null;

  const handleClose: MouseEventHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    closeUpgradeStrip();
  };

  const handleUpgradeStripClick: MouseEventHandler = (e) => {
    if (user?.hasNetworkSubscription) return;

    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
  };

  const defaultUpgradeStripText = (
    <>
      <PhoneText>
        10 SITES - 1 PRICE &nbsp;&nbsp;<Underline>SAVE 80% NOW</Underline>
      </PhoneText>
      <TabletText>
        OUR 10 MOST POPULAR SITES - 1 LOW PRICE &nbsp;&nbsp;<Underline>SAVE 80% NOW</Underline>
      </TabletText>
      <DesktopText>
        OUR 10 MOST POPULAR SITES - 1 LOW PRICE &nbsp;&nbsp;<Underline>SAVE 80% NOW</Underline>
      </DesktopText>
    </>
  );

  const hasNetworkSubscriptionText = (
    <>
      <PhoneText>“Members Only” specials and upgrades</PhoneText>
      <TabletText>Visit our discounts area to find great “Members Only” specials and upgrades</TabletText>
      <DesktopText>Visit our discounts area to find great “Members Only” specials and upgrades</DesktopText>
    </>
  );

  const textToShow = user?.hasNetworkSubscription ? hasNetworkSubscriptionText : defaultUpgradeStripText;

  return (
    <UpgradeStripWrapper>
      <StyledJoinLink href={joinUrl} onClick={handleUpgradeStripClick}>
        {textToShow}
        <CloseIcon onClick={handleClose} icon={faTimesCircle} />
      </StyledJoinLink>
      <MetartNetworkModal show={showModal} onHide={() => setShowModal(false)} />
    </UpgradeStripWrapper>
  );
};

export const UpgradeStripWrapper = styled.div`
  background-color: #f1cc1d;
  text-align: center;
  color: #000;
  text-decoration: none;
  font-weight: 700;
`;

const PhoneText = styled.span`
  display: inline;

  @media ${TABLET_PORTRAIT} {
    display: none;
  }
`;

const TabletText = styled.span`
  display: none;

  @media ${TABLET_PORTRAIT} {
    display: inline;
  }

  @media ${DESKTOP} {
    display: none;
  }
`;

const DesktopText = styled.span`
  display: none;

  @media ${DESKTOP} {
    display: inline;
  }
`;

const Underline = styled.span`
  text-decoration: underline;
`;

const StyledJoinLink = styled.a`
  padding: 4px 8px;
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;

  &:hover,
  &:active,
  &:focus {
    color: #333;
    text-decoration: none;
  }
`;

const CloseIcon = styled(FontAwesomeIcon)`
  cursor: pointer;
  font-size: 1.3rem;
  margin-left: 8px;

  &:hover {
    color: #fff;
  }
`;
